import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  externalFolderShare,
  fetchFolderExternalShareDetail,
  fetchMatterExternalShareDetail,
  revokeFolderShare,
  revokeMatterShares,
} from "../service/externalFolderShare";

// Mirrors the relevant slice of MatterDetailsSlice.js's external-share
// thunks/state, but standalone: a shared folder can be either matter-scoped
// (opened from the matter tree) or personal (opened from My Files), and
// MatterDetailsSlice assumes a matter is loaded.
//
// Each thunk takes the thing the screen was opened from — `{ folder_id }`
// from a folder, `{ matter_id }` from the top of a client's file — and
// asks the matching endpoint. The screen itself does not care which.

export const fetchFolderExternalShareDetails = createAsyncThunk(
  "folderExternalShareDetails/fetch",
  async ({ folder_id, matter_id }, { rejectWithValue }) => {
    try {
      const response = matter_id
        ? await fetchMatterExternalShareDetail({ matter_id })
        : await fetchFolderExternalShareDetail({ folder_id });
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitFolderExternalShare = createAsyncThunk(
  "submitFolderExternalShare/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await externalFolderShare(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const revokeFolderExternalShare = createAsyncThunk(
  "revokeFolderExternalShare/update",
  async ({ folder_id, matter_id }, { rejectWithValue }) => {
    try {
      const response = matter_id
        ? await revokeMatterShares({ matter_id })
        : await revokeFolderShare({ folder_id });
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  folderShareDetails: null,
  status: "idle",
};

const externalFolderShareSlice = createSlice({
  name: "externalFolderShare",
  initialState,
  reducers: {
    resetFolderShareDetails: (state) => {
      state.folderShareDetails = null;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolderExternalShareDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchFolderExternalShareDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.folderShareDetails = action.payload.data;
      })
      .addCase(fetchFolderExternalShareDetails.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { resetFolderShareDetails } = externalFolderShareSlice.actions;
export default externalFolderShareSlice.reducer;
